import { AnimatedSprite, Assets, Container, Spritesheet, Texture } from "pixi.js";

import { effectSeconds, FX_SHEET } from "../../../shared/Fx";

/**
 * A field of one-shot animations: the machinery every effect in the `fxA` sheet needs, with
 * nothing in it about what any particular effect means.
 *
 * There are two callers and they could hardly be less alike — a muzzle flash is a child of the
 * boat that fired, pinned to a barrel by the pivot the artist authored; a splash is a world
 * coordinate on open water, pinned by its own centre. What they share is all of this: a pool of
 * `AnimatedSprite`s, a list of live ones, and a clock that steps frames off elapsed seconds. The
 * second copy of that is the one that would quietly stop matching the first.
 *
 * Whoever owns a `Bursts` decides where its `container` hangs, and therefore what the coordinates
 * handed to `spawn` mean. This file never knows.
 */

/** How a burst is registered to the point it is spawned at. */
export type Pin =
    /**
     * The pivot TexturePacker stored on the frame, re-applied on every frame change. For art
     * authored against a fixed point that is not its middle — a muzzle flash blooming out of a
     * barrel, whose frames grow in one direction only.
     */
    | "anchor"
    /**
     * The middle of the untrimmed box, ignoring any stored pivot. For art that is radially
     * symmetric and grows in every direction at once — a splash, whose centre *is* its impact
     * point by construction, so there is no pivot for an artist to author and a stored one is
     * more likely to be an unset zero than a decision.
     */
    | "centre";

/** One animation in flight, and how far through it is. */
interface Burst {
    sprite: AnimatedSprite;
    age: number;
    life: number;
    frames: number;
    /** Where it was spawned, kept for a `Place` that has to re-derive the sprite's position. */
    x: number;
    y: number;
}

/**
 * Where a burst should sit this frame, given where it was spawned.
 *
 * Optional, and only one of the two callers needs it. A muzzle flash is a child of the boat that
 * fired, so the deck coordinates it was spawned at stay true for its whole life and nothing has
 * to move it. A splash is a **world** coordinate drawn against a camera that keeps moving, so the
 * screen position it had when it was spawned is wrong by the next frame — its owner re-projects
 * it here rather than baking a screen coordinate in.
 */
export type Place = (sprite: AnimatedSprite, x: number, y: number) => void;

/** Each prefix's roster, resolved once off the loaded sheet. */
const rosters = new Map<string, string[]>();

/**
 * Every animation in the `fxA` sheet under one prefix, in sheet order — the roster of one kind of
 * effect. `muzzle_` is the flashes, `water_splash` is the splashes; see `MUZZLE_PREFIX` and
 * `SPLASH_PREFIX` in shared/Fx.ts.
 *
 * Read from the loaded spritesheet rather than from `config/fx.json`, so the sheet stays the
 * answer to what exists — the same rule the FX panel is built on. Resolved on first use and not
 * at module load: this runs inside a preloaded scene, and an empty roster cached before the
 * `game` bundle finished would never refill.
 */
export function effectsUnder(prefix: string): string[] {
    const known = rosters.get(prefix);
    if (known) return known;

    const sheet = Assets.get<Spritesheet>(FX_SHEET);
    if (!sheet) return [];

    const names = Object.keys(sheet.animations).filter((name) => name.startsWith(prefix));
    rosters.set(prefix, names);

    return names;
}

/**
 * A roll in `[0, 1)` for an event, off its id.
 *
 * The arena mints shot ids as consecutive integers, and consecutive integers through a plain
 * modulo would march the art round in strict rotation — which reads as a pattern rather than as
 * variety the moment a captain holds the trigger down. Multiplying into a large odd constant
 * first scatters them, and it is still a pure function of the id, so every client draws the same.
 */
export function rollFor(id: number): number {
    return (Math.imul(id | 0, 2654435761) >>> 0) / 4294967296;
}

export class Bursts {
    private readonly _container = new Container();
    private readonly _live: Burst[] = [];
    /** Spent sprites, kept for the next one rather than destroyed and remade. */
    private readonly _pool: AnimatedSprite[] = [];

    public get container(): Container {
        return this._container;
    }

    /**
     * Starts one animation, in whatever space this field's container sits in.
     *
     * `length` is how long the art is drawn, in that space's pixels, measured across the long side
     * of its **first** frame's untrimmed box — first frame rather than each frame, because the
     * frames of an effect are wildly different sizes as it blooms and dies, and fitting each to
     * its own box would pulse it instead of growing it. It is the same rule the dashboard previews
     * by and the sails are drawn by.
     *
     * Does nothing for art the sheet does not hold, which is what a stale `config/fx.json` names.
     */
    public spawn(name: string, x: number, y: number, rotation: number, length: number, fps: number, pin: Pin): void {
        const sheet = Assets.get<Spritesheet>(FX_SHEET);
        const textures = sheet?.animations[name];
        if (!textures || textures.length === 0) return;

        const sprite = this._take(textures, pin);

        // `texture.width`/`height` are the **untrimmed** size — Pixi's `orig` — so this is the
        // art's full extent regardless of how tightly the packer cropped the frame.
        const reference = Math.max(textures[0].width, textures[0].height);
        sprite.scale.set(reference > 0 ? length / reference : 0);
        sprite.position.set(x, y);
        sprite.rotation = rotation;
        sprite.visible = true;

        this._live.push({ sprite, age: 0, life: effectSeconds({ fps }, textures.length), frames: textures.length, x, y });
    }

    /**
     * Steps every live burst and retires the ones that have burned out.
     *
     * Frames are stepped off elapsed seconds rather than by Pixi's own ticker, the way the wake
     * and the sails are: `autoUpdate` would run them off `Ticker.shared` at whatever the render
     * rate happens to be, and these are authored at an fps that has to mean frames per second on
     * any machine.
     *
     * `place` is for an owner whose coordinates move under it — see `Place`.
     */
    public update(deltaSeconds: number, place?: Place): void {
        for (let i = this._live.length - 1; i >= 0; i--) {
            const burst = this._live[i];
            burst.age += deltaSeconds;

            if (burst.life <= 0 || burst.age >= burst.life) {
                burst.sprite.visible = false;
                this._pool.push(burst.sprite);
                this._live.splice(i, 1);
                continue;
            }

            // Clamped rather than wrapped: this is a one-shot, and the last frame is where it
            // ends. `currentFrame` throws outside [0, totalFrames - 1].
            burst.sprite.currentFrame = Math.min(burst.frames - 1, Math.floor((burst.age / burst.life) * burst.frames));
            place?.(burst.sprite, burst.x, burst.y);
        }
    }

    public destroy(): void {
        this._container.destroy({ children: true });
        this._live.length = 0;
        this._pool.length = 0;
    }

    /**
     * A sprite for one burst, pooled and re-pointed at this animation's frames — which art came up
     * is a fresh draw every time, so a pooled sprite is rarely showing what it showed last.
     *
     * `updateAnchor` is what pins an `"anchor"` burst: TexturePacker stored a pivot on every frame
     * of the sheet, Pixi reads it into `Texture.defaultAnchor`, and this copies it onto the sprite
     * on every frame change — including the ones `update` drives by hand. A `"centre"` burst turns
     * that off and sits at 0.5, because its stored pivot is not a decision worth honouring.
     */
    private _take(textures: Texture[], pin: Pin): AnimatedSprite {
        const pooled = this._pool.pop();

        if (pooled) {
            pooled.updateAnchor = pin === "anchor";
            pooled.textures = textures;
            this._register(pooled, textures[0], pin);
            return pooled;
        }

        const sprite = new AnimatedSprite({ textures, autoUpdate: false, updateAnchor: pin === "anchor" });
        this._register(sprite, textures[0], pin);
        this._container.addChild(sprite);

        return sprite;
    }

    /**
     * Sets the anchor for the frame a burst starts on. `textures` resets playback to frame 0 but
     * not the anchor, and the first frame of a different animation has a different pivot — so
     * without this the first frame of every pooled burst wears the last one's registration.
     */
    private _register(sprite: AnimatedSprite, first: Texture, pin: Pin): void {
        if (pin === "centre" || !first.defaultAnchor) sprite.anchor.set(0.5);
        else sprite.anchor.copyFrom(first.defaultAnchor);
    }
}
